import React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { UIFieldShell, useUIField } from "../form";

const radioIcon = <span className="ui-theme-radio-icon" />;
const radioCheckedIcon = (
  <span className="ui-theme-radio-icon ui-theme-radio-icon--checked">
    <span className="ui-theme-radio-dot" />
  </span>
);

const UIRadioGroup = ({
  label,
  optionalLabel,
  required,
  helperText,
  error,
  id,
  name,
  value,
  onChange,
  onBlur,
  options,
  row,
  canSelectOption,
  disabled,
}) => {
  const fieldId = id || name;
  const field = useUIField({
    name,
    value,
    checked: value || false,
    error,
    helperText,
  });

  return (
    <UIFieldShell
      id={fieldId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={field.helperText}
      error={field.showError}
      classNames={{
        root: "ui-radio-group__wrapper",
      }}
    >
      <FormControl error={field.showError} disabled={disabled}>
        <RadioGroup
          id={fieldId}
          row={row}
          name={name}
          value={field.value ?? ""}
          onChange={(event) => {
            if (disabled) return;
            const nextValue = event.target.value;
            if (canSelectOption && !canSelectOption(nextValue)) {
              return;
            }
            field.setValue(nextValue);
            onChange?.(nextValue, event);
          }}
          onBlur={(event) => {
            field.setTouched(true);
            onBlur?.(event);
          }}
        >
          {options.map((option) => (
            <FormControlLabel
              className="ui-radio-label"
              key={option.value}
              value={option.value}
              control={
                <Radio
                  className="ui-radio"
                  disabled={disabled}
                  icon={radioIcon}
                  checkedIcon={radioCheckedIcon}
                />
              }
              label={option.label}
              disabled={disabled}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </UIFieldShell>
  );
};

UIRadioGroup.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ),
  row: PropTypes.bool,
  /** Return false to block updating the field (keeps previous selection). */
  canSelectOption: PropTypes.func,
  disabled: PropTypes.bool,
};

UIRadioGroup.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: undefined,
  id: undefined,
  name: undefined,
  value: undefined,
  onChange: undefined,
  onBlur: undefined,
  options: [],
  row: false,
  canSelectOption: undefined,
  disabled: false,
};

export default UIRadioGroup;
